import React from 'react';
import { ActivityLogModel } from '../../../../models';
import { User, Calendar, Clock, Activity, Key } from 'lucide-react';

interface LogListProps {
    logs: ActivityLogModel[];
    isLoading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

const LogList: React.FC<LogListProps> = ({ logs, isLoading, hasMore, onLoadMore }) => {

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleString();
    };

    const getActionLabel = (action: string) => {
        const labels: Record<string, string> = {
            'extract_omr': 'OMR 채점',
            'generate_counsel_reply': '상담 답변 생성',
            'generate_qa_answer': '질문 답변 생성',
            'create_schedules': '일정 생성',
            'update_schedule': '일정 수정',
            'delete_schedule': '일정 삭제',
            'bulk_delete_schedules': '일정 일괄 삭제',
        };
        return labels[action] || action;
    };

    const getTypeColor = (type: string) => {
        if (type === 'api_usage') return 'bg-purple-50 text-purple-700 border-purple-100';
        if (type === 'schedule_change') return 'bg-blue-50 text-blue-700 border-blue-100';
        return 'bg-slate-50 text-slate-700 border-slate-100';
    };

    const renderDetails = (log: ActivityLogModel) => {
        try {
            const parsed = JSON.parse(log.details);
            if (log.type === 'api_usage') {
                return (
                    <div className="text-xs text-slate-500 space-y-1">
                        {parsed.model && <div><span className="font-bold">모델:</span> {parsed.model}</div>}
                        {parsed.fileSize && <div><span className="font-bold">파일크기:</span> {(parsed.fileSize / 1024).toFixed(1)}KB</div>}
                        {parsed.questionSnippet && <div><span className="font-bold">질문:</span> {parsed.questionSnippet}...</div>}
                        {parsed.hasImage !== undefined && <div><span className="font-bold">이미지:</span> {parsed.hasImage ? '포함' : '미포함'}</div>}
                        {parsed.counselId && <div><span className="font-bold">상담ID:</span> {parsed.counselId}</div>}
                    </div>
                );
            }
            if (log.type === 'schedule_change') {
                return (
                    <div className="text-xs text-slate-500 space-y-1">
                        {parsed.count && <div><span className="font-bold">개수:</span> {parsed.count}개</div>}
                        {parsed.task && <div><span className="font-bold">내용:</span> {parsed.task}</div>}
                        {parsed.status && <div><span className="font-bold">상태변경:</span> {parsed.status}</div>}
                        {parsed.date && <div><span className="font-bold">날짜변경:</span> {new Date(parsed.date).toLocaleDateString()}</div>}
                    </div>
                );
            }
            return <div className="text-xs text-slate-500 truncate">{log.details}</div>;
        } catch (e) {
            return <div className="text-xs text-slate-500 truncate">{log.details}</div>;
        }
    };

    if (isLoading && logs.length === 0) {
        return (
            <div className="flex justify-center items-center py-20 text-slate-400">
                <div className="animate-spin mr-2 w-5 h-5 border-2 border-slate-300 border-t-indigo-500 rounded-full"></div>
                로딩 중...
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Activity size={48} className="mb-4 opacity-20" />
                <p>활동 기록이 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="overflow-y-auto flex-1 custom-scrollbar p-2">
                <table className="w-full text-left">
                    <thead className="sticky top-0 bg-white z-10 text-xs font-black text-slate-500 uppercase tracking-wider border-b border-slate-100">
                        <tr>
                            <th className="px-4 py-3 w-40">일시</th>
                            <th className="px-4 py-3 w-32">사용자</th>
                            <th className="px-4 py-3 w-32">유형</th>
                            <th className="px-4 py-3 w-40">활동</th>
                            <th className="px-4 py-3 w-32">API 키</th>
                            <th className="px-4 py-3">상세 내용</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {logs.map((log) => (
                            <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3 text-xs text-slate-600 font-medium">
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={12} className="text-slate-400" />
                                        {formatDate(log.createdAt)}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-[10px]">
                                            {log.actorName[0]}
                                        </div>
                                        <div className="text-xs font-bold text-slate-700">{log.actorName}</div>
                                    </div>
                                    <div className="text-[10px] text-slate-400 ml-7.5">{log.actorRole}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${getTypeColor(log.type)}`}>
                                        {log.type === 'api_usage' ? 'API 사용' : '일정 변경'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs font-bold text-slate-700">
                                    {getActionLabel(log.action)}
                                </td>
                                <td className="px-4 py-3">
                                    {log.apiKeyPart ? (
                                        <div className="flex items-center gap-1.5 text-xs font-mono text-slate-600">
                                            <Key size={12} className="text-amber-500" />
                                            •••• {log.apiKeyPart}
                                        </div>
                                    ) : (
                                        <span className="text-slate-300 text-xs">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    {renderDetails(log)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {hasMore && (
                    <div className="p-4 flex justify-center border-t border-slate-50">
                        <button
                            onClick={onLoadMore}
                            disabled={isLoading}
                            className="text-sm font-bold text-indigo-600 hover:text-indigo-700 hover:underline disabled:opacity-50"
                        >
                            {isLoading ? '로딩 중...' : '더 보기'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LogList;
